import React from "react";
import classNames from "classnames";

import { Props, Flag as F, FlagMap } from "./types";
import { Flag } from "./flag";
import { Context, useInfiniteScroll } from "./utils";
import styles from "./styles.scss";

function canScrollElement(element: HTMLDivElement) {
  return element && element.scrollHeight > element.clientHeight;
}

function InfiniteScrollRoot<ProvidedFlag extends F>(
  props: Omit<
    Props<ProvidedFlag>,
    "children" | "style" | "id" | "className"
  > & { children: React.ReactNode },
) {
  const flags = React.useRef(props.flags || []);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const canScroll = ref.current && canScrollElement(ref.current);

  React.useEffect(() => {
    let interval: null | number = null;

    if (props.canFetchOnIdle && !canScroll) {
      interval = window.setInterval(() => {
        const anchors = Array.from(
          document.querySelectorAll("[data-infinite-scroll]"),
        ) as HTMLDivElement[];

        anchors.forEach((element) => {
          const screenHeight = window.innerHeight;
          const positionY = element.getBoundingClientRect().y;
          const flag = flags.current.find(
            (flag) => element.dataset.infiniteScrollFlagName === flag.name,
          );
          const isDisabled =
            typeof element.dataset.infiniteScrollFlagDisabled !== "undefined" ||
            (flag && flag.isDisabled);

          !isDisabled &&
            positionY >= 0 &&
            positionY <= screenHeight &&
            flag &&
            flag.retrieveItems();
        });
      }, 200);
    }

    return () => {
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, [props.canFetchOnIdle, canScroll, flags]);

  React.useEffect(() => {
    if (!canScroll || !ref.current) return;

    const el = ref.current;
    const scroll = (e: Event) => {
      if (props.scroll && e.currentTarget) {
        const target = e.currentTarget as HTMLDivElement;

        props.scroll(target.scrollTop);
      }

      const anchors = Array.from(
        document.querySelectorAll("[data-infinite-scroll]"),
      ) as HTMLDivElement[];

      anchors.forEach((element) => {
        const screenHeight = window.innerHeight;
        const positionY = element.getBoundingClientRect().y;
        const flag = flags.current.find(
          (flag) => element.dataset.infiniteScrollFlagName === flag.name,
        );
        const isDisabled =
          typeof element.dataset.infiniteScrollFlagDisabled !== "undefined" ||
          (flag && flag.isDisabled);

        !isDisabled &&
          positionY >= 0 &&
          positionY <= screenHeight &&
          flag &&
          flag.retrieveItems();
      });
    };

    el.addEventListener("scroll", scroll);

    return () => {
      el.removeEventListener("scroll", scroll);
    };
  }, [canScroll, flags]);

  return (
    <Context.Provider
      value={{
        ref,
        flags: flags.current,
        registerFlag(flag) {
          const anchors = Array.from(
            document.querySelectorAll("[data-infinite-scroll]"),
          ) as HTMLDivElement[];
          const element = anchors.find(
            ({ dataset }) => dataset.infiniteScrollFlagName === flag.name,
          );

          if (element) {
            const screenHeight = window.innerHeight;
            const positionY = element.getBoundingClientRect().y;
            const isDisabled =
              typeof element.dataset.infiniteScrollFlagDisabled !==
                "undefined" ||
              (flag && flag.isDisabled);

            !isDisabled &&
              positionY >= 0 &&
              positionY <= screenHeight &&
              flag &&
              flag.retrieveItems();
          }

          flags.current = [...flags.current, flag as ProvidedFlag];
        },
        removeFlag(name) {
          flags.current = flags.current.filter((flag) => flag.name !== name);
        },
      }}
    >
      {props.children}
    </Context.Provider>
  );
}

function InfiniteScroll<ProvidedFlag extends F>(props: Props<ProvidedFlag>) {
  const flags = React.useRef(props.flags || []);
  const ref = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    let interval: null | number = null;

    if (props.canFetchOnIdle) {
      interval = window.setInterval(() => {
        // Checked each time: the content can shrink without a render here, like when folding a tree.
        if (ref.current && canScrollElement(ref.current)) return;

        const anchors = Array.from(
          document.querySelectorAll("[data-infinite-scroll]"),
        ) as HTMLDivElement[];

        anchors.forEach((element) => {
          const screenHeight = window.innerHeight;
          const positionY = element.getBoundingClientRect().y;
          const flag = flags.current.find(
            (flag) => element.dataset.infiniteScrollFlagName === flag.name,
          );
          const isDisabled =
            typeof element.dataset.infiniteScrollFlagDisabled !== "undefined" ||
            (flag && flag.isDisabled);

          !isDisabled &&
            positionY >= 0 &&
            positionY <= screenHeight &&
            flag &&
            flag.retrieveItems();
        });
      }, 200);
    }

    return () => {
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, [props.canFetchOnIdle, flags]);

  return (
    <Context.Provider
      value={{
        ref,
        flags: flags.current,
        registerFlag(flag) {
          const anchors = Array.from(
            document.querySelectorAll("[data-infinite-scroll]"),
          ) as HTMLDivElement[];
          const element = anchors.find(
            ({ dataset }) => dataset.infiniteScrollFlagName === flag.name,
          );

          if (element) {
            const screenHeight = window.innerHeight;
            const positionY = element.getBoundingClientRect().y;
            const isDisabled =
              typeof element.dataset.infiniteScrollFlagDisabled !==
                "undefined" ||
              (flag && flag.isDisabled);

            !isDisabled &&
              positionY >= 0 &&
              positionY <= screenHeight &&
              flag &&
              flag.retrieveItems();
          }

          flags.current = [...flags.current, flag as ProvidedFlag];
        },
        removeFlag(name) {
          flags.current = flags.current.filter((flag) => flag.name !== name);
        },
      }}
    >
      <div className={styles.infiniteScroll}>
        <div
          ref={ref}
          id={props.id}
          className={classNames(styles.content, {
            [props.className || ""]: props.className,
          })}
          style={props.style}
          onScroll={(e) => {
            props.scroll && props.scroll(e.currentTarget.scrollTop);
            const anchors = Array.from(
              document.querySelectorAll("[data-infinite-scroll]"),
            ) as HTMLDivElement[];

            anchors.forEach((element) => {
              const screenHeight = window.innerHeight;
              const positionY = element.getBoundingClientRect().y;
              const flag = flags.current.find(
                (flag) => element.dataset.infiniteScrollFlagName === flag.name,
              );
              const isDisabled =
                typeof element.dataset.infiniteScrollFlagDisabled !==
                  "undefined" ||
                (flag && flag.isDisabled);

              !isDisabled &&
                positionY >= 0 &&
                positionY <= screenHeight &&
                flag &&
                flag.retrieveItems();
            });
          }}
        >
          {props.children(
            Object.values(flags).reduce<FlagMap<ProvidedFlag>>(
              (map, { name }) => ({
                ...map,
                [name]: (
                  <div
                    data-infinite-scroll
                    data-infinite-scroll-flag-name={name}
                  />
                ),
              }),
              {} as FlagMap<ProvidedFlag>,
            ),
          )}
        </div>
      </div>
    </Context.Provider>
  );
}

export { InfiniteScroll, InfiniteScrollRoot, useInfiniteScroll, Flag };
